import { useEffect, useState, useRef } from "react";
import type { IPlatformAdapter } from "../types";
import { message } from "dingtalk-design-desktop";
// @ts-ignore

const TABLE_NAME = "消息模板表";
const FIELD_NAME = "模板名称";
const FIELD_CONTENT = "模板内容";

// 获取全局 Dingdocs 对象
const getGlobalDingdocs = (): any => {
  if (typeof window !== "undefined" && (window as any).Dingdocs) {
    return (window as any).Dingdocs;
  }
  return null;
};

export interface MessageTemplate {
  id: string;
  name: string;
  content: string;
}

export function useMessageTemplates(_platform?: IPlatformAdapter) {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const Dingdocs = getGlobalDingdocs();
      if (!Dingdocs?.script?.run) {
        console.warn("[模板调试] Dingdocs 不可用，无法读取模板");
        setTemplates([]);
        return;
      }
      const records = await Dingdocs.script.run("getMessageTemplates");
      console.log("[模板调试] fetchTemplates 读取到的 records:", records);
      // 兼容字段名为id/模板名称/模板内容/Title/content等
      const result = (records || [])
        .filter((rec: any) => {
          const fields = rec.fields || {};
          const name =
            fields[FIELD_NAME] ||
            fields["模板名称"] ||
            fields["title"] ||
            fields["Title"];
          const content =
            fields[FIELD_CONTENT] ||
            fields["模板内容"] ||
            fields["content"] ||
            fields["Content"];
          return name && name.trim() && content && content.trim();
        })
        .map((rec: any) => {
          const fields = rec.fields || {};
          return {
            id: rec.id,
            name:
              fields[FIELD_NAME] ||
              fields["模板名称"] ||
              fields["title"] ||
              fields["Title"] ||
              "",
            content:
              fields[FIELD_CONTENT] ||
              fields["模板内容"] ||
              fields["content"] ||
              fields["Content"] ||
              "",
          };
        });
      console.log("[模板调试] fetchTemplates 过滤/转换后 result:", result);
      setTemplates(result);
    } catch (e: any) {
      console.error("[模板调试] fetchTemplates 出错:", e);
      setError(e.message || "读取模板失败");
    } finally {
      setLoading(false);
    }
  };

  const addTemplate = async (name: string, content: string) => {
    setLoading(true);
    setError(null);
    try {
      const Dingdocs = getGlobalDingdocs();
      if (!Dingdocs?.script?.run) throw new Error("平台不支持写入操作");
      // 先查重，直接获取最新数据
      const records = await Dingdocs.script.run("getMessageTemplates");
      console.log("[模板调试] addTemplate 查重时读取到的 records:", records);
      const exists = (records || []).some((rec: any) => {
        const fields = rec.fields || {};
        const recName =
          fields[FIELD_NAME] ||
          fields["模板名称"] ||
          fields["title"] ||
          fields["Title"] ||
          "";
        return recName === name;
      });
      if (exists) {
        setError("模板名称已存在");
        message.error("模板名称已存在，请重新命名");
        setLoading(false);
        throw new Error("模板名称已存在");
      }
      // 获取消息模板表sheetId
      const sheets = await Dingdocs.script.run("getAllSheets");
      const tplSheet = sheets.find((s: any) => s.name === TABLE_NAME);
      if (!tplSheet) throw new Error("未找到消息模板表");
      await Dingdocs.script.run(
        "addRecordToSheet",
        { [FIELD_NAME]: name, [FIELD_CONTENT]: content },
        tplSheet.id
      );
      await fetchTemplates();
      message.success("模板保存成功");
    } catch (e: any) {
      console.error("[模板调试] addTemplate 出错:", e);
      setError(e.message || "新增模板失败");
      throw e;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 延迟执行，确保 Dingdocs 已初始化
    const timer = setTimeout(() => {
      fetchTemplates();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return { templates, loading, error, fetchTemplates, addTemplate };
}
